// ─── Lecture d'un relevé bancaire (OFX / CSV) — module PUR ────────
// Porté de MoPCal `services/import.js`. Parsing 100 % JS : aucune dépendance
// native, donc livrable en OTA. Aucun accès Firestore ici.
//
// Décodage : le natif (Hermes) n'a pas de TextDecoder « windows-1252 » et lit en
// UTF-8 par défaut → accents cassés sur les relevés (souvent en windows-1252).
// On décode donc nous-mêmes, à partir des OCTETS BRUTS :
//   • OFX → windows-1252 (jamais de l'UTF-8 en pratique) ;
//   • CSV → UTF-8 si valide, sinon repli windows-1252.
// Web et natif passent par la MÊME fonction, pour un comportement identique.

// Normalise un libellé banque en MOTIF stable d'un mois à l'autre : minuscules,
// sans accents, sans chiffres (dates/numéros) ni ponctuation.
//   « PRET IMMOBILIER ECH 08/06/26 » → « pret immobilier ech »
export function motifDeLibelle(libelle) {
  return String(libelle || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[0-9]+/g, ' ')
    .replace(/[^a-z ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function hashCourt(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

// ── Décodage octets → texte ──────────────────────────────────────
const CP1252_HAUT = [
  0x20AC, 0x0081, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021,
  0x02C6, 0x2030, 0x0160, 0x2039, 0x0152, 0x008D, 0x017D, 0x008F,
  0x0090, 0x2018, 0x2019, 0x201C, 0x201D, 0x2022, 0x2013, 0x2014,
  0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x009D, 0x017E, 0x0178,
];
function decodeWin1252(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    s += String.fromCharCode(b >= 0x80 && b <= 0x9F ? CP1252_HAUT[b - 0x80] : b);
  }
  return s;
}
function estUtf8Valide(b) {
  let i = 0; const n = b.length;
  while (i < n) {
    const c = b[i];
    if (c < 0x80) { i++; continue; }
    let len;
    if (c >= 0xC2 && c <= 0xDF) len = 2;
    else if (c >= 0xE0 && c <= 0xEF) len = 3;
    else if (c >= 0xF0 && c <= 0xF4) len = 4;
    else return false;
    if (i + len > n) return false;
    for (let k = 1; k < len; k++) { if ((b[i + k] & 0xC0) !== 0x80) return false; }
    i += len;
  }
  return true;
}
function decodeUtf8(bytes) {
  if (typeof TextDecoder !== 'undefined') {
    try { return new TextDecoder('utf-8').decode(bytes); } catch (_) { /* repli manuel */ }
  }
  let s = ''; let i = 0; const n = bytes.length;
  while (i < n) {
    const c = bytes[i++];
    if (c < 0x80) s += String.fromCharCode(c);
    else if (c >= 0xC0 && c < 0xE0) s += String.fromCharCode(((c & 0x1F) << 6) | (bytes[i++] & 0x3F));
    else if (c >= 0xE0 && c < 0xF0) { const c2 = bytes[i++], c3 = bytes[i++]; s += String.fromCharCode(((c & 0x0F) << 12) | ((c2 & 0x3F) << 6) | (c3 & 0x3F)); }
    else { const c2 = bytes[i++], c3 = bytes[i++], c4 = bytes[i++]; const cp = (((c & 0x07) << 18) | ((c2 & 0x3F) << 12) | ((c3 & 0x3F) << 6) | (c4 & 0x3F)) - 0x10000; s += String.fromCharCode(0xD800 + (cp >> 10), 0xDC00 + (cp & 0x3FF)); }
  }
  return s;
}
// bytes : Uint8Array. format : 'ofx' | 'csv'.
export function decoderReleve(bytes, format) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (format === 'ofx') return decodeWin1252(b);
  return estUtf8Valide(b) ? decodeUtf8(b) : decodeWin1252(b);
}

// ── Parsers ──────────────────────────────────────────────────────
const isoDepuisJJMMAAAA = (d) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(d || '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

// OFX (SGML) : blocs <STMTTRN>…</STMTTRN>, valeurs « tag puis texte » sans balise
// fermante. On extrait DTPOSTED, TRNAMT (signé), NAME/MEMO, FITID.
export function parseOFX(texte) {
  const blocs = String(texte || '').split(/<STMTTRN>/i).slice(1).map(b => b.split(/<\/STMTTRN>/i)[0]);
  const tag = (b, t) => { const m = new RegExp('<' + t + '>([^\\r\\n<]*)', 'i').exec(b); return m ? m[1].trim() : ''; };
  const ops = [];
  for (const b of blocs) {
    const amtRaw = tag(b, 'TRNAMT');
    if (!amtRaw) continue;
    const montant = parseFloat(amtRaw.replace(',', '.'));
    if (!isFinite(montant) || montant === 0) continue;
    const dt = (tag(b, 'DTPOSTED') || '').slice(0, 8);
    const date = dt.length === 8 ? `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}` : '';
    if (!date) continue;
    const nom = tag(b, 'NAME');
    const memo = tag(b, 'MEMO');
    const libelle = (memo && memo !== nom ? `${nom} ${memo}` : (nom || memo)).trim();
    const fitid = tag(b, 'FITID');
    ops.push({ date, montant, libelle, ref: `ofx:${fitid || `${date}|${montant}|${libelle}`}` });
  }
  return ops;
}

// CSV MULTI-BANQUES : séparateur et EN-TÊTE auto-détectés, colonnes reconnues par
// leur NOM (date, montant, débit/crédit, libellé/intitulé/nature…) quelle que soit
// la banque. Décimale « , » ou « . » ; montant signé OU colonnes débit/crédit.
// Repli sur le format LCL historique (colonnes fixes) si aucun en-tête reconnu.
function splitLigneCSV(l, sep) {
  const out = []; let cur = ''; let q = false;
  for (let i = 0; i < l.length; i++) {
    const ch = l[i];
    if (ch === '"') { if (q && l[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
    else if (ch === sep && !q) { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out.map(s => s.trim());
}
function nombreFr(s) {
  let t = String(s == null ? '' : s).trim();
  if (!t) return NaN;
  const neg = /^\(.*\)$/.test(t) || /-/.test(t);
  t = t.replace(/[^0-9.,]/g, '');
  if (t.indexOf(',') > -1 && t.indexOf('.') > -1) {
    t = (t.lastIndexOf(',') > t.lastIndexOf('.')) ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  } else if (t.indexOf(',') > -1) t = t.replace(',', '.');
  const n = parseFloat(t);
  if (!isFinite(n)) return NaN;
  return neg ? -Math.abs(n) : n;
}
function dateIsoSouple(s) {
  const t = String(s || '').trim();
  let m = /^(\d{2})[/.](\d{2})[/.](\d{4})$/.exec(t); if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t); if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{2})[/.](\d{2})[/.](\d{2})$/.exec(t); if (m) return `20${m[3]}-${m[2]}-${m[1]}`;
  return '';
}
const sansAccents = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
function detecterSepCSV(lignes) {
  let best = ';', bestN = 1;
  for (const sep of [';', '\t', ',']) {
    const n = (lignes[0] || '').split(sep).length;
    if (n > bestN) { bestN = n; best = sep; }
  }
  return best;
}
function mapperColonnes(entete) {
  const h = entete.map(sansAccents);
  const find = (re) => h.findIndex(x => re.test(x));
  let date = h.findIndex(x => /date/.test(x) && /(oper|compt)/.test(x));
  if (date < 0) date = find(/date/);
  let libelle = find(/(libell|label|intitul|nature|motif|descript|designation|tiers|beneficiaire)/);
  if (libelle < 0) libelle = h.findIndex(x => /(oper|detail|name|categ)/.test(x) && !/date/.test(x));
  return { date, libelle, montant: find(/montant|amount/), debit: find(/debit/), credit: find(/credit/) };
}
export function parseCSV(texte) {
  const lignes = String(texte || '').replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  if (!lignes.length) return [];
  const sep = detecterSepCSV(lignes);
  let enteteIdx = -1, cols = null;
  for (let i = 0; i < Math.min(5, lignes.length); i++) {
    const c = mapperColonnes(splitLigneCSV(lignes[i], sep));
    if (c.date >= 0 && (c.montant >= 0 || c.debit >= 0 || c.credit >= 0) && c.libelle >= 0) { enteteIdx = i; cols = c; break; }
  }
  if (enteteIdx < 0) return parseCSVLcl(texte);
  const vus = {}; const ops = [];
  for (let i = enteteIdx + 1; i < lignes.length; i++) {
    const c = splitLigneCSV(lignes[i], sep);
    const date = dateIsoSouple(c[cols.date]);
    let montant;
    if (cols.montant >= 0) montant = nombreFr(c[cols.montant]);
    else {
      const d = cols.debit >= 0 ? nombreFr(c[cols.debit]) : NaN;
      const cr = cols.credit >= 0 ? nombreFr(c[cols.credit]) : NaN;
      montant = (isFinite(d) && d !== 0) ? -Math.abs(d) : (isFinite(cr) ? Math.abs(cr) : NaN);
    }
    if (!date || !isFinite(montant) || montant === 0) continue;   // soldes / lignes vides
    const libelle = (c[cols.libelle] || '').trim() || `Opération du ${c[cols.date] || date}`;
    const base = `csv:${date}|${montant}|${libelle}`;
    vus[base] = (vus[base] || 0) + 1;
    ops.push({ date, montant, libelle, ref: vus[base] > 1 ? `${base}#${vus[base]}` : base });
  }
  return ops;
}
// Repli : format LCL historique (sans en-tête, colonnes fixes).
function parseCSVLcl(texte) {
  const lignes = String(texte || '').replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  const vus = {}; const ops = [];
  for (const l of lignes) {
    const c = l.split(';');
    const date = isoDepuisJJMMAAAA(c[0]);
    const montant = nombreFr(c[1]);
    const libelle = ((c[4] || '').trim()) || ((c[5] || '').trim());
    if (!date || !isFinite(montant) || montant === 0 || !libelle) continue;
    const base = `csv:${date}|${montant}|${libelle}`;
    vus[base] = (vus[base] || 0) + 1;
    ops.push({ date, montant, libelle, ref: vus[base] > 1 ? `${base}#${vus[base]}` : base });
  }
  return ops;
}

// Identité du COMPTE portée par un relevé OFX (`<BANKACCTFROM>`) : numéro de
// compte et code banque. Permet de reconnaître le compte d'un import à l'autre.
// Le SOLDE OFFICIEL annoncé par la banque (OFX : LEDGERBAL/BALAMT + DTASOF).
// C'est la seule information du fichier qu'on laissait sur la table (16/08) :
// elle permet le rapprochement bancaire — comparer ce que l'app calcule à ce
// que la banque affirme, au centime.
export function soldeDuReleve(texte) {
  const bloc = /<LEDGERBAL>([\s\S]*?)(<\/LEDGERBAL>|<AVAILBAL>|$)/i.exec(String(texte || ''));
  if (!bloc) return null;
  const lire = (tag) => { const m = new RegExp('<' + tag + '>([^\r\n<]*)', 'i').exec(bloc[1]); return m ? m[1].trim() : ''; };
  const montant = parseFloat(lire('BALAMT').replace(',', '.'));
  const dt = lire('DTASOF').slice(0, 8);
  const date = dt.length === 8 ? `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}` : '';
  if (!Number.isFinite(montant) || !date) return null;
  return { montant: Math.round(montant * 100) / 100, date };
}

// Le LENDEMAIN d'une date ISO. Sert d'ancre après recalage : le solde de la
// banque est celui d'APRÈS les opérations du jour — le compte repart donc du
// jour suivant (`soldeCompteA` compte les opérations depuis `dateSolde` INCLUS).
export function lendemainISO(dateISO) {
  const [y, m, j] = String(dateISO || '').split('-').map(Number);
  if (!y || !m || !j) return '';
  const d = new Date(Date.UTC(y, m - 1, j + 1));
  return d.toISOString().slice(0, 10);
}

// Le CSV n'a pas cette information — d'où le choix manuel la première fois.
export function compteDuReleve(texte) {
  const t = String(texte || '');
  const lire = (tag) => { const m = new RegExp('<' + tag + '>([^\\r\\n<]*)', 'i').exec(t); return m ? m[1].trim() : ''; };
  const acctid = lire('ACCTID');
  return acctid ? { acctid, banque: lire('BANKID'), type: lire('ACCTTYPE') } : null;
}

export function detecterFormat(nomFichier, texte) {
  if (/\.(ofx|qfx)$/i.test(nomFichier || '')) return 'ofx';
  if (/\.csv$/i.test(nomFichier || '')) return 'csv';
  return /<OFX>|<STMTTRN>/i.test(texte || '') ? 'ofx' : 'csv';
}

export const parserReleve = (nomFichier, texte) =>
  (detecterFormat(nomFichier, texte) === 'ofx' ? parseOFX(texte) : parseCSV(texte));
